import axios from 'axios';
import config from '../app/config';
import { getAccessTokenForAgent } from '../auth/getAccessTokenForAgent';
import { AgentConfig, ManifestEntry } from '../types/agent.types';

/**
 * Derive a stable ID from the agent's name.
 * Lowercases, replaces spaces/special chars with hyphens.
 */
function deriveAgentId(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Map a ManifestEntry from the API to an AgentConfig used throughout the app.
 */
function mapManifestToAgentConfig(entry: ManifestEntry): AgentConfig {
  const { manifest, container_url, running } = entry;
  const { app_client_id } = manifest;

  const suggestions = (manifest.most_asked_questions ?? []).map((question) => ({
    icon: '💬',
    title: question,
    description: question,
  }));

  const patchNotes = manifest.patch_log
    ? manifest.patch_log.split('\n').filter(Boolean)
    : [];

  return {
    id: deriveAgentId(manifest.name),
    name: manifest.name,
    description: manifest.description,
    icon: 'agents',
    apiEndpoint: container_url,
    requiredScopes: [`api://${app_client_id}/user_impersonation`],
    clientId: app_client_id,
    version: manifest.version,
    patchNotes,
    suggestions,
    imageBase64: manifest.image || undefined,
    status: running,
    redirectUrl: entry.redirect_url,
  };
}

/**
 * Fetch agent manifests from the AgentManifestLocator API and map them
 * to AgentConfig objects.
 *
 * Authenticates using the platform-level scope from the environment config.
 */
export async function fetchManifests(onManifest: (agent: AgentConfig) => void): Promise<void> {
  const scope = config.azureAdScopeIdForApi;
  if (!scope) {
    throw new Error('manifestApi: REACT_APP_AZURE_AD_SCOPE_ID_FOR_API is not configured');
  }

  const token = await getAccessTokenForAgent([scope]);

  const response = await fetch(`${config.manifestApiUrl}/manifests`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch manifests: ${response.statusText}`);
  }

  if (!response.body) {
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const jsonStr = line.slice(6);
        try {
          const entry: ManifestEntry = JSON.parse(jsonStr);
          const agentConfig = mapManifestToAgentConfig(entry);
          onManifest(agentConfig);
        } catch (e) {
          console.error('Failed to parse SSE data:', e, line);
        }
      }
    }
  }
}

/**
 * Call POST /match to find the agent whose description best matches
 * the user's natural-language query.
 */
export async function matchAgent(query: string): Promise<{ agent_name: string; reason: string }> {
  const scope = config.azureAdScopeIdForApi;
  if (!scope) {
    throw new Error('manifestApi: REACT_APP_AZURE_AD_SCOPE_ID_FOR_API is not configured');
  }

  const token = await getAccessTokenForAgent([scope]);

  const response = await axios.post<{ agent_name: string; reason: string }>(
    `${config.manifestApiUrl}/match`,
    { query },
    { headers: { Authorization: `Bearer ${token}` } },
  );

  return response.data;
}
